<template>
  <div class="sales-partner-revenue-report">
    <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div>
        <label class="form-label small text-muted mb-1 d-block"
          >Period (by sale date)</label
        >
        <DateRangePickerComponent
          v-model="dateRange"
          placeholder="Last 12 months"
          @select="onDateSelect"
          @clear="resetToLastYear"
        />
      </div>
      <p class="small text-muted mb-0 ms-md-3 explainer">
        Financial dashboard figures for all business of the companies a partner
        referred (and swap deals it sourced), whether or not the referral still
        earns commission. <b>Gross revenue</b> is what buyers pay,
        <b>net revenue</b> is Soldera's margin, and <b>net after referral</b>
        deducts the referral bonuses paid on it. Open sales count at their
        predicted values, as on the dashboard.
        <template v-if="report.dashboard_net_revenue != null">
          The dashboard also counts direct business no partner brought in, so
          each card shows the partners' share of the dashboard's settled +
          pending total — its headline cards show settled only.
        </template>
      </p>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Gross revenue</div>
          <div class="fs-4 fw-bold">{{ formatEuro(report.gross_revenue) }}</div>
          <div
            v-if="report.dashboard_gross_revenue != null"
            class="small text-muted"
          >
            {{
              dashboardShare(
                report.gross_revenue,
                report.dashboard_gross_revenue
              )
            }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Net revenue</div>
          <div class="fs-4 fw-bold">
            {{ formatEuro(report.net_revenue) }}
          </div>
          <div
            v-if="report.dashboard_net_revenue != null"
            class="small text-muted"
          >
            {{
              dashboardShare(report.net_revenue, report.dashboard_net_revenue)
            }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Net after referral</div>
          <div class="fs-4 fw-bold text-success">
            {{ formatEuro(report.net_after_referral) }}
          </div>
          <div
            v-if="report.dashboard_net_after_referral != null"
            class="small text-muted"
          >
            {{
              dashboardShare(
                report.net_after_referral,
                report.dashboard_net_after_referral
              )
            }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Earning partners</div>
          <div class="fs-4 fw-bold">
            {{ earningPartnersCount }} / {{ report.partners.length }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-muted py-4 text-center">Loading…</div>
    <div
      v-else-if="report.partners.length === 0"
      class="text-muted py-4 text-center"
    >
      No sales partners found.
    </div>
    <div v-else class="table-responsive">
      <table class="table table-sm table-hover align-middle mb-0">
        <thead class="table-light">
          <tr class="small text-muted">
            <th class="text-end">#</th>
            <th
              v-for="column in columns"
              :key="column.key"
              class="sortable"
              :class="{ 'text-end': column.numeric }"
              @click="sortBy(column.key)"
            >
              {{ column.label }}
              <i v-if="sortKey === column.key" :class="sortIconClass"></i>
            </th>
            <th class="share-column">Share of net revenue</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedPartners"
            :key="row.referrer_company_id"
          >
            <td class="text-end text-muted">{{ index + 1 }}</td>
            <td>{{ row.referrer_company_name ?? "Unknown partner" }}</td>
            <td>
              <span
                class="badge rounded-pill"
                :class="row.sales_partner_type ? 'bg-primary' : 'bg-secondary'"
                >{{ row.sales_partner_type ?? "Referrer" }}</span
              >
            </td>
            <td class="text-end">{{ row.referred_companies_count }}</td>
            <td class="text-end">{{ row.sales_count }}</td>
            <td class="text-end">{{ formatMwh(row.volume_mwh) }}</td>
            <td class="text-end">{{ formatEuro(row.gross_revenue) }}</td>
            <td class="text-end fw-bold">{{ formatEuro(row.net_revenue) }}</td>
            <td class="text-end text-muted">
              {{ formatEuro(row.referral_cost) }}
            </td>
            <td class="text-end">
              {{ formatEuro(row.net_after_referral) }}
            </td>
            <td>
              <div class="progress share-bar" role="presentation">
                <div
                  class="progress-bar bg-success"
                  :style="{ width: `${sharePercent(row)}%` }"
                ></div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import DateRangePickerComponent from "@/components/common/DateRangePickerComponent.vue";
import type { DateRange } from "@/components/common/dateRange";
import { getSalesPartnerRevenue } from "@/admin/commission/commission";
import { formatEuro, formatMwh } from "@/composables/formatUtils";
import type { SalesPartnerRevenue, SalesPartnerRevenueRow } from "@/types";
import { orderBy, sumBy } from "lodash-es";
import { computed, inject, onMounted, ref } from "vue";

type SortKey = keyof Pick<
  SalesPartnerRevenueRow,
  | "referrer_company_name"
  | "sales_partner_type"
  | "referred_companies_count"
  | "sales_count"
  | "volume_mwh"
  | "gross_revenue"
  | "net_revenue"
  | "referral_cost"
  | "net_after_referral"
>;

const columns: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: "referrer_company_name", label: "Sales partner", numeric: false },
  { key: "sales_partner_type", label: "Partner type", numeric: false },
  { key: "referred_companies_count", label: "Companies", numeric: true },
  { key: "sales_count", label: "Sales", numeric: true },
  { key: "volume_mwh", label: "MWh sold", numeric: true },
  { key: "gross_revenue", label: "Gross revenue", numeric: true },
  { key: "net_revenue", label: "Net revenue", numeric: true },
  { key: "referral_cost", label: "Referral cost", numeric: true },
  { key: "net_after_referral", label: "Net after referral", numeric: true },
];

const globalErrorHandler = inject("globalErrorHandler") as (
  error: unknown
) => void;

const dateRange = ref<DateRange | null>(lastTwelveMonths());
const loading = ref(false);
const report = ref<SalesPartnerRevenue>({
  date_from: "",
  date_to: "",
  partners: [],
  gross_revenue: 0,
  net_revenue: 0,
  referral_cost: 0,
  net_after_referral: 0,
  dashboard_gross_revenue: null,
  dashboard_net_revenue: null,
  dashboard_net_after_referral: null,
});
const sortKey = ref<SortKey>("net_revenue");
const sortDirection = ref<"asc" | "desc">("desc");

const sortedPartners = computed(() =>
  orderBy(
    report.value.partners,
    [(row) => row[sortKey.value] ?? ""],
    [sortDirection.value]
  )
);

const earningPartnersCount = computed(
  () => report.value.partners.filter((row) => row.gross_revenue !== 0).length
);

// Shares are of the partners' summed net, not the deduplicated total, so the bars add up to 100%.
const summedNetRevenue = computed(() =>
  sumBy(report.value.partners, (row) => Math.max(row.net_revenue, 0))
);

const sortIconClass = computed(() =>
  sortDirection.value === "asc" ? "bi bi-sort-up" : "bi bi-sort-down"
);

onMounted(() => loadReport());

function lastTwelveMonths(): DateRange {
  const today = new Date();
  const yearAgo = new Date(today);
  yearAgo.setFullYear(today.getFullYear() - 1);
  return [yearAgo, today];
}

const sharePercent = (row: SalesPartnerRevenueRow) =>
  summedNetRevenue.value > 0
    ? (Math.max(row.net_revenue, 0) / summedNetRevenue.value) * 100
    : 0;

const dashboardShare = (partnersTotal: number, dashboardTotal: number) =>
  dashboardTotal > 0
    ? `${Math.round((partnersTotal / dashboardTotal) * 100)}% of ${formatEuro(dashboardTotal)} on the dashboard`
    : `Dashboard: ${formatEuro(dashboardTotal)}`;

const sortBy = (key: SortKey) => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortDirection.value = "desc";
  }
};

const resetToLastYear = () => {
  dateRange.value = lastTwelveMonths();
  loadReport();
};

// The range picker fires on its first click too, with only the start set; wait for the end.
const onDateSelect = () => {
  if (dateRange.value?.[1]) {
    loadReport();
  }
};

// Only the latest request may write the report or report an error, so a slow earlier range can't
// overwrite a newer one.
let latestRequest = 0;

const loadReport = async () => {
  const [dateFrom, dateTo] = dateRange.value ?? [null, null];
  const request = ++latestRequest;
  loading.value = true;
  try {
    const result = await getSalesPartnerRevenue(
      dateFrom ?? null,
      dateTo ?? null
    );
    if (request === latestRequest) {
      report.value = result;
    }
  } catch (error) {
    if (request === latestRequest) {
      globalErrorHandler(error);
    }
  } finally {
    if (request === latestRequest) {
      loading.value = false;
    }
  }
};
</script>

<style scoped lang="scss">
.explainer {
  max-width: 640px;
}

.sortable {
  cursor: pointer;
}

.share-column {
  min-width: 160px;
}

.share-bar {
  height: 8px;
}
</style>
